import React, { useRef, useState, useCallback, useEffect, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { mentionCandidatesService, type MentionCandidate } from '../../services/mention-candidates';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minRows?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

/** How long a pause in the typing ends a query. */
const SEARCH_DEBOUNCE_MS = 180;

/**
 * A comment box that completes @handles.
 *
 * The candidates are asked of the server on every query rather than filtered out
 * of a list held here: who can be tagged is everybody the user follows plus
 * everybody who follows them, which is not a list an autocomplete can hold a
 * page of and still be right — see mentionCandidatesService.
 */
export default function MentionTextarea({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  minRows = 2,
  onKeyDown: externalKeyDown,
}: MentionTextareaProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionSearch, setMentionSearch] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number>(-1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [candidates, setCandidates] = useState<MentionCandidate[]>([]);

  // The previous answer stays on screen while the next one is in flight, so the
  // list does not blink away between two keystrokes.
  useEffect(() => {
    if (mentionSearch === null) {
      setCandidates([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      mentionCandidatesService.search(mentionSearch).then((rows) => {
        if (cancelled) return;
        setCandidates(rows);
        setActiveIndex(0);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mentionSearch]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursor = e.target.selectionStart ?? newValue.length;

    // An @ sequence still open before the cursor is the one being typed.
    const textBeforeCursor = newValue.slice(0, cursor);
    const atMatch = textBeforeCursor.match(/@([a-zA-Z0-9_]*)$/);

    if (atMatch) {
      setMentionSearch(atMatch[1]);
      setMentionStart(cursor - atMatch[0].length);
      setActiveIndex(0);
    } else {
      setMentionSearch(null);
      setMentionStart(-1);
    }

    onChange(newValue);
  }, [onChange]);

  const insertMention = useCallback((user: MentionCandidate) => {
    const username = user.attributes.username ?? '';
    const slug = user.attributes.slug;
    const mention = `@[${username}](${slug})`;

    // Replaces from mentionStart up to the current cursor position
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    const before = value.slice(0, mentionStart);
    const after = value.slice(cursor);
    const newValue = `${before}${mention} ${after}`;

    onChange(newValue);
    setMentionSearch(null);
    setMentionStart(-1);

    // Puts the cursor back after the mention
    setTimeout(() => {
      const newCursor = mentionStart + mention.length + 1;
      textareaRef.current?.setSelectionRange(newCursor, newCursor);
      textareaRef.current?.focus();
    }, 0);
  }, [value, mentionStart, onChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    // While the @mention dropdown is open, the arrows belong to it
    if (mentionSearch !== null && candidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, candidates.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        return;
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(candidates[activeIndex]);
        return;
      } else if (e.key === 'Escape') {
        setMentionSearch(null);
        setMentionStart(-1);
        return;
      }
    }

    // Otherwise hand it to the caller (e.g. Cmd+Enter to submit)
    externalKeyDown?.(e);
  }, [mentionSearch, candidates, activeIndex, insertMention, externalKeyDown]);

  return (
    <div className={cn('relative', className)}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? t('comments.placeholder')}
        disabled={disabled}
        rows={minRows}
      />

      {mentionSearch !== null && candidates.length > 0 && (
        <div className="absolute z-50 bottom-full mb-1 w-64 rounded-md border bg-popover shadow-md overflow-hidden">
          {candidates.map((user, idx) => (
            <button
              key={user.attributes.slug}
              type="button"
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent transition-colors',
                idx === activeIndex && 'bg-accent'
              )}
              onMouseDown={(ev) => {
                ev.preventDefault();
                insertMention(user);
              }}
            >
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarImage src={user.attributes.avatar_url ?? undefined} />
                <AvatarFallback className="text-xs">
                  {(user.attributes.username ?? 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium">@{user.attributes.username}</span>
              {user.attributes.full_name && (
                <span className="text-muted-foreground truncate text-xs">{user.attributes.full_name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
